import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { TASK_STATUS_STYLES } from "../lib/constants";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Link2, MessageSquare, Send } from "lucide-react";

export default function WorkstreamDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, effectiveRole } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null); // {type:'finding'|'mitigation', id}
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [reqForm, setReqForm] = useState({ status: "", phase: "", note: "" });

  const canEdit = effectiveRole === "pmo" || (effectiveRole === "it_executive" && user?.workstream_id === id);
  const canResolve = ["pmo", "it_admin"].includes(effectiveRole);

  const load = useCallback(() => {
    api.get(`/workstreams/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load"));
    api.get(`/comments?entity_type=workstream&entity_id=${id}`).then((r) => setComments(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-[#5A687C]">Loading…</div>;
  const { workstream: w, findings, mitigations, links, tasks, milestones, raid, budget } = data;

  const linkedMitIds = (fid) => links.filter((l) => l.finding_id === fid).map((l) => l.mitigation_id);
  const linkedFndIds = (mid) => links.filter((l) => l.mitigation_id === mid).map((l) => l.finding_id);
  const isHighlighted = (type, itemId) => {
    if (!sel) return false;
    if (sel.type === type && sel.id === itemId) return true;
    if (sel.type === "finding" && type === "mitigation") return linkedMitIds(sel.id).includes(itemId);
    if (sel.type === "mitigation" && type === "finding") return linkedFndIds(sel.id).includes(itemId);
    return false;
  };
  const dim = (type, itemId) => sel && !isHighlighted(type, itemId);

  const addItem = async (coll) => {
    const text = prompt(`New ${coll === "findings" ? "finding" : "mitigation"}:`);
    if (!text) return;
    try { await api.post(`/${coll}`, { workstream_id: id, text }); toast.success("Added"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delItem = async (coll, itemId) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/${coll}/${itemId}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleLink = async (fid, mid) => {
    const existing = links.find((l) => l.finding_id === fid && l.mitigation_id === mid);
    try {
      if (existing) await api.delete(`/links/${existing.id}`);
      else await api.post("/links", { finding_id: fid, mitigation_id: mid });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const postComment = async () => {
    if (!commentText.trim()) return;
    try { await api.post("/comments", { entity_type: "workstream", entity_id: id, text: commentText }); setCommentText(""); toast.success("Comment posted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const resolveComment = async (cid) => {
    try { await api.post(`/comments/${cid}/resolve`); toast.success("Marked resolved — the executive was notified"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const submitChange = async () => {
    const changes = {};
    if (reqForm.status) changes.status = reqForm.status;
    if (reqForm.phase) changes.phase = reqForm.phase;
    if (Object.keys(changes).length === 0) return toast.error("Choose a status or phase to change");
    if (reqForm.note.trim().length < 10) return toast.error("Add a note (min 10 characters) for the PMO");
    changes.submitter_note = reqForm.note;
    try {
      const r = await api.patch(`/workstreams/${id}`, changes);
      toast.success(r.data.pending_approval ? "Submitted to the PMO approval queue" : "Updated");
      setReqForm({ status: "", phase: "", note: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5A687C] hover:text-[#1F2733]">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="text-xs font-bold text-white bg-[#BA0C2F] rounded-full px-2.5 py-1">{w.code}</span>
            <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733] mt-2">{w.title}</h1>
            <p className="text-sm text-[#5A687C] mt-1">{w.description}</p>
          </div>
          <div className="text-right space-y-2">
            <StatusBadge status={w.status} />
            <div className="text-xs text-[#5A687C]">Phase · <span className="font-semibold text-[#1F2733]">{w.phase}</span></div>
            {w.published ? <div className="text-xs font-semibold text-[#1A5633]">● Live on dashboard</div> : <div className="text-xs font-semibold text-[#824D0E]">● Draft (not published)</div>}
          </div>
        </div>
      </div>

      {effectiveRole === "it_executive" && user?.workstream_id === id && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6" data-testid="request-change-panel">
          <h2 className="font-bold text-[#1F2733] mb-1">Request a change</h2>
          <p className="text-xs text-[#5A687C] mb-4">Propose a status or phase change. A note is required and your request routes to the PMO for approval.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <select data-testid="req-status" value={reqForm.status} onChange={(e) => setReqForm({ ...reqForm, status: e.target.value })} className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm bg-white">
              <option value="">Status (unchanged)</option>
              {["On Track", "At Risk", "Critical", "Not Started"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input data-testid="req-phase" value={reqForm.phase} onChange={(e) => setReqForm({ ...reqForm, phase: e.target.value })} placeholder="Phase (unchanged)" className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
            <button data-testid="req-submit-btn" onClick={submitChange} className="bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-5 py-2.5 font-semibold">Submit for approval</button>
          </div>
          <textarea data-testid="req-note" value={reqForm.note} onChange={(e) => setReqForm({ ...reqForm, note: e.target.value })} rows={2} placeholder="Why is this change needed? (required, min 10 chars)" className="w-full mt-3 px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
        </div>
      )}

      {sel && (
        <div className="flex items-center gap-2 text-sm bg-[#FBEAED] border border-[#F1B9C3] text-[#8A0822] rounded-full px-4 py-2 w-fit">
          <Link2 className="h-4 w-4" /> Highlighting links for selected item.
          <button onClick={() => setSel(null)} className="font-bold hover:underline ml-2">Clear</button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {/* Findings */}
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1F2733]">Assessment findings</h2>
            {canEdit && <button data-testid="add-finding-btn" onClick={() => addItem("findings")} className="inline-flex items-center gap-1 text-sm font-semibold text-[#BA0C2F] hover:underline"><Plus className="h-4 w-4" /> Add</button>}
          </div>
          <div className="space-y-3">
            {findings.map((f) => (
              <div key={f.id} data-testid={`finding-${f.id}`} onClick={() => setSel({ type: "finding", id: f.id })}
                className={`rounded-2xl border p-4 cursor-pointer transition-all ${isHighlighted("finding", f.id) ? "border-[#BA0C2F] bg-[#FBEAED] shadow-md" : dim("finding", f.id) ? "border-[#DCE2EA] opacity-40" : "border-[#DCE2EA] hover:border-[#C4CEDB]"}`}>
                <div className="flex items-start gap-3">
                  <span className="text-xs font-extrabold text-white bg-[#BA0C2F] rounded-full h-7 w-7 flex items-center justify-center shrink-0">F{f.index}</span>
                  <div className="flex-1">
                    <p className="text-sm text-[#1F2733]">{f.text}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <StatusBadge status={f.status === "Open" ? "At Risk" : "On Track"} />
                      <span className="text-[11px] text-[#98A2AF]">→ {linkedMitIds(f.id).map((m) => "M" + (mitigations.find((x) => x.id === m)?.index)).join(", ") || "unlinked"}</span>
                    </div>
                  </div>
                  {canEdit && <button onClick={(e) => { e.stopPropagation(); delItem("findings", f.id); }} className="text-[#98A2AF] hover:text-[#BA0C2F]"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mitigations */}
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1F2733]">Mitigation plan</h2>
            {canEdit && <button data-testid="add-mitigation-btn" onClick={() => addItem("mitigations")} className="inline-flex items-center gap-1 text-sm font-semibold text-[#BA0C2F] hover:underline"><Plus className="h-4 w-4" /> Add</button>}
          </div>
          <div className="space-y-3">
            {mitigations.map((m) => (
              <div key={m.id} data-testid={`mitigation-${m.id}`} onClick={() => setSel({ type: "mitigation", id: m.id })}
                className={`rounded-2xl border p-4 cursor-pointer transition-all ${isHighlighted("mitigation", m.id) ? "border-[#2E8B57] bg-[#ECF8F1] shadow-md" : dim("mitigation", m.id) ? "border-[#DCE2EA] opacity-40" : "border-[#DCE2EA] hover:border-[#C4CEDB]"}`}>
                <div className="flex items-start gap-3">
                  <span className="text-xs font-extrabold text-white bg-[#2E8B57] rounded-full h-7 w-7 flex items-center justify-center shrink-0">M{m.index}</span>
                  <div className="flex-1">
                    <p className="text-sm text-[#1F2733]">{m.text}</p>
                    {canEdit && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {findings.map((f) => {
                          const linked = linkedMitIds(f.id).includes(m.id);
                          return (
                            <button key={f.id} onClick={(e) => { e.stopPropagation(); toggleLink(f.id, m.id); }}
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${linked ? "bg-[#BA0C2F] text-white border-[#BA0C2F]" : "border-[#DCE2EA] text-[#98A2AF] hover:border-[#BA0C2F]"}`}>
                              F{f.index}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {canEdit && <button onClick={(e) => { e.stopPropagation(); delItem("mitigations", m.id); }} className="text-[#98A2AF] hover:text-[#BA0C2F]"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tasks */}
      <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-[#1F2733]">Action items</h2>
          <button data-testid="open-task-board" onClick={() => nav(`/workstream/${id}/tasks`)} className="text-sm font-semibold text-[#BA0C2F] hover:underline">Open task board →</button>
        </div>
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F4F6FA]">
              <div className="flex-1 min-w-0"><div className="text-sm text-[#1F2733] truncate">{t.title}</div>
                <div className="text-[11px] text-[#98A2AF]">{t.start_date} → {t.end_date}</div></div>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${TASK_STATUS_STYLES[t.status] || ""}`}>{t.status}</span>
            </div>
          ))}
          {tasks.length === 0 && <div className="text-sm text-[#5A687C]">No tasks yet.</div>}
        </div>
      </div>

      {/* Comments */}
      <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
        <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-4"><MessageSquare className="h-5 w-5 text-[#BA0C2F]" /> Comments &amp; questions</h2>
        <div className="flex gap-2 mb-4">
          <input data-testid="comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && postComment()} placeholder="Leave a comment or question for the PMO…"
            className="flex-1 px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
          <button data-testid="comment-submit-btn" onClick={postComment} className="bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-5 font-semibold inline-flex items-center gap-1.5"><Send className="h-4 w-4" /> Post</button>
        </div>
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-[#FBEAED] text-[#BA0C2F] flex items-center justify-center text-xs font-bold shrink-0">{c.user_name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
              <div className="bg-[#F4F6FA] rounded-2xl px-4 py-2.5 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-bold text-[#1F2733]">{c.user_name} <span className="text-[#98A2AF] font-normal">· {new Date(c.created_at).toLocaleString()}</span></div>
                  {c.resolved ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#ECF8F1] text-[#1A5633]">Resolved</span>
                  ) : canResolve ? (
                    <button data-testid={`resolve-comment-${c.id}`} onClick={() => resolveComment(c.id)}
                      className="text-[10px] font-bold px-2.5 py-0.5 rounded-full border border-[#BA0C2F] text-[#BA0C2F] hover:bg-[#FBEAED]">Mark resolved</button>
                  ) : null}
                </div>
                <div className="text-sm text-[#1F2733] mt-0.5">{c.text}</div>
              </div>
            </div>
          ))}
          {comments.length === 0 && <div className="text-sm text-[#5A687C]">No comments yet.</div>}
        </div>
      </div>
    </div>
  );
}
